<template lang="html">
  <form-step
    :complete='complete'
    :step='step'
    :icon='icon'
    :title='title'
    v-model='valid'
    @submit='submit'>
    <v-layout row wrap>
      <v-flex xs12>
        <v-switch
          v-model='tripType'
          :label='`${(tripType ? "Round Trip" : "One Way")}`'/>
      </v-flex>
      <v-flex xs12>
        <AutocompleteComponent
          idSuffix="0"
          name="start"
          label="Pickup Location"
          v-model="pickup"
          icon="mdi-home"
          :raiseError="raiseError"/>
      </v-flex>
      <v-flex
        xs12
        v-for='id in additionalStops'
        :key='id'>
        <AutocompleteComponent
          :idSuffix="`${id}`"
          name="stop"
          :label="`Stop ${id}`"
          :key="id"
          v-model="stops[id - 1]"
          icon="mdi-map-marker-plus"
          :raiseError="raiseError"/>
      </v-flex>
      <v-flex xs12>
        <v-btn
          flat
          icon
          color='success'
          @click='addStop'>
          <v-icon>mdi-plus-circle</v-icon>
        </v-btn>
        <v-btn
          flat
          icon
          color='error'
          v-if='additionalStops.length > 0'
          @click='removeStop'>
          <v-icon>mdi-minus-circle</v-icon>
        </v-btn>
      </v-flex>
      <v-flex xs12>
        <AutocompleteComponent
          :idSuffix="additionalStops.length + 1"
          name="dest"
          label="Destination Location"
          v-model="destination"
          icon="mdi-flag"
          :raiseError="raiseError"/>
      </v-flex>
      <v-flex xs12>
        <v-container fluid fill-height grid-list-xl>
          <v-layout justify-center wrap>
            <v-flex xs12 md4 class='pl-0'>
              <v-text-field
                prepend-inner-icon='mdi-account-group'
                mask='###'
                label='Passengers'
                v-model='passengers'
                :rules='passengerRules'/>
            </v-flex>
            <v-flex xs12 md1>
              <v-container fluid>
                <v-layout row wrap>
                  <v-icon>mdi-wheelchair-accessibility</v-icon>
                </v-layout>
                <v-layout row wrap>
                  <v-switch v-model='wheelchair'/>
                </v-layout>
                <v-dialog
                  ref='dialog'
                  v-model='dialog'
                  :return-value.sync='passengers'
                  persistent lazy width='25%'>
                  <v-card
                    color="secondary"
                    dark
                    class="text-xs-center">
                    <v-card-title class='headline'> Number of Wheelchairs </v-card-title>
                    <v-card-text>
                      <v-text-field
                        prepend-inner-icon='mdi-wheelchair-accessibility'
                        mask='#'
                        v-model='wheelchairs'
                        label='Wheelchairs'
                        name='wheelchairs'/>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        flat
                        color="error"
                        @click="wheelchair = false">
                        Cancel
                      </v-btn>
                      <v-btn
                        flat
                        color="success"
                        @click="$refs.dialog.save(wheelchairs)">
                        OK
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-container>
            </v-flex>
            <v-flex xs12 md7>
              <v-select
                label='Amenities'
                prepend-inner-icon='mdi-shape-plus'
                v-model='amenities'
                :items='availableAmenities'
                attach
                chips
                multiple/>
            </v-flex>
          </v-layout>
        </v-container>
      </v-flex>
      <v-flex xs12>
        <v-textarea outline label='Additional Information' v-model='notes'>
        </v-textarea>
      </v-flex>
      <v-flex xs12>
        <v-spacer>
        </v-spacer>
        <v-btn
          color='primary'
          type='submit'
          :loading='loading'
          :disabled='disabled'>
          Continue
        </v-btn>
      </v-flex>
    </v-layout>
  </form-step>
</template>

<script>
import AutocompleteComponent from './AutoComplete/AutocompleteComponent'
import FormStep from './FormStep'
import { mapGetters, mapActions } from 'vuex'
export default {
  components: {
    'form-step': FormStep,
    AutocompleteComponent
  },
  props: {
    complete: {
      type: Boolean,
      default: false
    },
    step: {
      type: [ Number, String ],
      default: 2
    },
    planner: {
      type: Object,
      default: {}
    },
  },
  data: vm => ({
    loading: false,
    icon: 'mdi-map-marker-distance',
    title: 'Trip Info',
    additionalStops: [],
    nextStopId: 1,
    tripType: true,
    pickup: null,
    destination: null,
    stops: [],
    amenities: [],
    passengers: null,
    wheelchair: false,
    wheelchairs: 0,
    notes: '',
    dialog: false,
    valid: true,
    passengerRules: [ v => !!v || 'Number of Passengers required']
  }),
  watch: {
    wheelchair: {
      immediate: true,
      handler (val) {
        this.dialog = val
        if (!val) {
          this.wheelchairs = 0
        }
      }
    }
  },
  computed: {
    disabled () {
      return !this.valid || !!this.loading
    },
    availableAmenities () {
      return this.allAmenities.map(item => {
        return { value: item.id, text: item.name }
      });
    },
    payload () {
      return {
        trip_planner: this.planner,
        operator: this.operator,
        type: this.tripType ? 'r' : 'o',
        stops: this.getStops(),
        amenities: this.amenities,
        wheelchairs: this.wheelchairs,
        passengers: this.passengers,
        notes: this.notes
      }
    },
    ...mapGetters('operator', { operator: 'id' }),
    ...mapGetters('amenities', { allAmenities: 'all' })
  },
  methods: {
    addStop () {
      this.additionalStops.push(this.nextStopId);
      this.nextStopId++
    },
    removeStop () {
      this.additionalStops.pop()
      this.stops.pop()
      this.nextStopId--
    },
    getStops () {
      this.stops.unshift(this.pickup)
      this.stops.push(this.destination)
      if (this.tripType) {
        this.stops.push(this.pickup)
      }
      return this.stops
    },
    resetData() {
      this.additionalStops = [];
      this.nextStopId = 1;
      this.tripType = true;
      this.pickup = null;
      this.destination = null;
      this.stops = [];
      this.amenities = [];
      this.passengers = null;
      this.wheelchair = false;
      this.wheelchairs = 0;
      this.notes = '';
    },
    submit() {
      this.loading = true
      delete this.payload.trip_planner.user
      return this.createTrip(this.payload).then(() => {
        this.$emit('continue')
        this.resetData()
        this.loading = false
      }).catch(e => {
        this.raiseError(e)
        this.loading = false
      })
    },
    ...mapActions('plan', [ 'createTrip' ]),
    ...mapActions('app', [ 'raiseError' ])
  }
}
</script>

<style lang="css" scoped>
</style>
